import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { useAccount } from "./AccountContext";
import { themeApi } from "../services/userApi";

const FeatureThemeContext = createContext(null);

/**
 * The app's feature theme (Basic/Advanced/Custom) -- distinct from color
 * scheme (light/dark), which lives in ColorSchemeContext. "Advanced"
 * features are gated both here (hasFeature) for UI show/hide and, again,
 * on the server (api/services/theme.py's require_feature_enabled) for the
 * actual 403 enforcement -- this context is UX convenience, not security.
 */
export function FeatureThemeProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [theme, setTheme] = useState("Basic");
  const [enabledFeatures, setEnabledFeatures] = useState(new Set());
  // The account's tier, and what each feature costs. Without these a locked
  // item can only say "not enabled" -- it cannot tell an upgrade from a
  // toggle the user switched off themselves (#243).
  const [tier, setTier] = useState("Free");
  /* Whether THIS person may see money on the account being worked
     in (#472). Defaults TRUE so a failed load never hides a
     screen from the person who owns it -- the common case by far,
     and the server refuses anyway if the answer is really no. */
  const [canSeeFinancials, setCanSeeFinancials] = useState(true);
  /* WHICH NON-HONEY PRODUCTS THIS ACCOUNT HAS (#430 slice 2).
     Empty until the theme loads, and empty is the right default: the
     other-products door appears only once the account has earned it, so
     failing closed hides a link rather than showing one to an empty
     room -- the Forecast lesson (#449). */
  const [otherProducts, setOtherProducts] = useState([]);
  const [featureTiers, setFeatureTiers] = useState({});
  /* The presets this account may choose, and the tier each needs
     (#485). Computed on the SERVER, because working out that "Hive
     Keeping" needs Harvester means taking the max min_tier across that
     preset's own features -- a calculation the client has no feature
     list to perform, and the second place it would go wrong. Empty
     until loaded; the picker falls back to the three originals so a
     failed load still offers Basic and Custom. */
  const [themes, setThemes] = useState([]);
  /* Whether to mention the new presets, and which ones (#487). NULL
     unless the server decided every condition holds -- the client does no
     tier arithmetic and keeps no "have I shown this" flag of its own. */
  const [themeNudge, setThemeNudge] = useState(null);
  /* What the person chose to SEE in an account they help in (#508).
     Both null unless they chose, which is "everything the account has".
     A DISPLAY narrowing only: hasFeature stays the account's entitlement,
     because navigation gated on the helper's own theme is #443. */
  /* Whether this person may define or change the account's lists, sizes
     and varietals (#509). Defaults TRUE like canSeeFinancials: the server
     refuses anyway, and a failed load must not hide a control from the
     owner. */
  const [canChangeLists, setCanChangeLists] = useState(true);
  const [helperTheme, setHelperTheme] = useState(null);
  const [displayFeatures, setDisplayFeatures] = useState(null);
  const [loading, setLoading] = useState(true);

  const { actingAccountId } = useAccount();

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const data = await themeApi.get();
      setTheme(data.theme);
      setEnabledFeatures(new Set(data.enabled_features || []));
      setTier(data.subscription_tier || "Free");
      setCanSeeFinancials(data.can_see_financials !== false);
      setOtherProducts(Array.isArray(data.other_products) ? data.other_products : []);
      setFeatureTiers(data.feature_tiers || {});
      setThemes(Array.isArray(data.themes) ? data.themes : []);
      setThemeNudge(data.theme_nudge ?? null);
      setCanChangeLists(data.can_change_lists !== false);
      setHelperTheme(data.helper_theme ?? null);
      setDisplayFeatures(Array.isArray(data.display_features) ? new Set(data.display_features) : null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, actingAccountId]);

    /*
     * REFETCH WHEN THE ACCOUNT CHANGES (#415).
     *
     * enabled_features used to be a union across every account that had
     * shared with you, so it was the same set whichever apiary you were
     * working in and never needed refreshing. #415 scoped it to the
     * account being worked in, which makes this dependency load-bearing:
     * without it, switching leaves the previous account's feature set in
     * place and the UI renders controls whose calls the server now
     * refuses -- screens that exist and 403 on use, which is worse than
     * either state alone.
     *
     * AccountProvider wraps this one in App.jsx, so the hook is
     * available here. That nesting is now a requirement rather than a
     * convenience.
     */
  useEffect(() => {
    /* A failed or timed-out load is not an uncaught error: it leaves the
       plan unknown, which lockReason now reports as no badge rather than
       "Off". Callers that await refresh() still see the rejection. */
    refresh().catch(() => {});
  }, [refresh]);

  const hasFeature = useCallback((name) => enabledFeatures.has(name), [enabledFeatures]);

  /**
   * EVERY one of these, because that is what the server requires.
   *
   * secure_endpoint's `feature` takes a string OR a tuple, and the tuple
   * means ALL -- it loops require_feature_enabled over each name and any
   * one of them refuses the request:
   *
   *     for feature_name in (feature,) if isinstance(feature, str) else feature:
   *         require_feature_enabled(...)
   *
   * The client had no way to say that. A nav item's `feature` list means
   * ANY (#316, for Reports, where the page renders whichever sections
   * you are entitled to) -- which is the OPPOSITE rule, and correct for
   * that case. So code checking one flag of a pair looked right and
   * called an endpoint the server would refuse.
   *
   * Found 3 Oct from console 403s on the dashboard: 25 endpoints are
   * gated on pairs, all of team.py among them, and `TeamCollaboration`
   * is Free since V113 (#445) while every team_* sub-flag is Beekeeper.
   * So a Free account passed the client's check and failed the server's.
   *
   * This exists so that condition is sayable rather than remembered.
   */
  /* For NAVIGATION ONLY: is this feature one the person wants shown?
     True for everything when they have not narrowed (#508). Never use it
     to decide whether a call is allowed -- that is hasFeature. */
  const isShown = useCallback(
    (name) => displayFeatures === null || displayFeatures.has(name),
    [displayFeatures]);

  const hasAllFeatures = useCallback(
    (...names) => names.flat().every((n) => enabledFeatures.has(n)),
    [enabledFeatures]);

  /**
   * Why is this feature off, in the user's terms?
   *
   *   "included"  -- your plan covers it; you switched it off (Settings)
   *   "Harvester" -- upgrading to that tier is what unlocks it
   *   "Business"  -- ditto
   *   "never"     -- the Unavailable sentinel; no plan grants it (the ai_*
   *                  flags), so never offer an upgrade for it
   *
   * Ranked rather than compared by name so adding a tier later does not
   * mean hunting for every string comparison.
   */
  const lockReason = useCallback((name) => {
    if (enabledFeatures.has(name)) return null;
    /* No tier list means the plan has not loaded (or the call failed or
       timed out). That is "unknown", and "Off" -- included in your plan but
       switched off -- is a claim: after a slow API start every item on a
       Free account said Off instead of naming its plan. No reason, no
       badge, until the server has answered. */
    if (Object.keys(featureTiers).length === 0) return null;
    const needed = featureTiers[name];
    if (!needed) return "included";
    if (needed === "Unavailable") return "never";
    const rank = { Free: 0, Harvester: 1, Business: 2 };
    return (rank[needed] ?? 0) > (rank[tier] ?? 0) ? needed : "included";
  }, [enabledFeatures, featureTiers, tier]);

  const value = useMemo(
    () => ({ theme, tier, enabledFeatures, hasFeature, lockReason, loading, refresh, setTheme,
              canSeeFinancials, otherProducts, hasAllFeatures, themes, themeNudge,
              helperTheme, isShown, canChangeLists }),
    [theme, tier, enabledFeatures, hasFeature, hasAllFeatures, lockReason, loading,
     refresh, canSeeFinancials, otherProducts, themes, themeNudge, helperTheme, isShown, canChangeLists]
  );

  return <FeatureThemeContext.Provider value={value}>{children}</FeatureThemeContext.Provider>;
}

export function useFeatureTheme() {
  const ctx = useContext(FeatureThemeContext);
  if (!ctx) throw new Error("useFeatureTheme must be used within FeatureThemeProvider");
  return ctx;
}
